import * as React from 'react'
import { Range, getTrackBackground, } from 'react-range'

interface SharedSliderProps {
  minValue: number,
  maxValue: number,
  step: number,
  handleChange: (values: number[]) => void,
  markLabels?: string[]|number[],
  id?: string,
}

interface SliderProps extends SharedSliderProps {
  values: number[]
  trackColors: string[]
  className?: string,
}

interface TwoThumbSliderProps extends SharedSliderProps {
  lowerValue: number,
  upperValue: number,
}

interface OneThumbSliderProps extends SharedSliderProps {
  value: number | '' | null | undefined,
  defaultValue: number,
  trackColors?: string[]
}

const Track = ({ style, innerRef, onMouseDown, onTouchStart, children, colors, values, minValue, maxValue, }) => {
  const background = getTrackBackground({
    values,
    colors,
    min: minValue,
    max: maxValue
  })

  return (
    // disabling lint on next line because the interaction is redundant
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="track-container"
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      style={style}
    >
      <div
        className="track"
        ref={innerRef}
        style={{ background, }}
      >
        {children}
      </div>
    </div>
  )
}

const Thumb = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div
    aria-label="Slider thumb"
    {...props}
    className="thumb"
    ref={ref}
  />
))
Thumb.displayName = 'Thumb'

interface MarkProps {
  index: number;
  markLabels: string[] | number[];
  minValue: number;
  step: number;
}

const Mark = React.forwardRef<HTMLDivElement, MarkProps>(({ index, markLabels, minValue, step, ...restProps }, ref) => {
  if (!markLabels) return null;
  
  const currentValue = minValue + (index * step);
  
  // Check if markLabels are numeric values that should match calculated values
  const firstLabel = markLabels[0];
  const isNumericLabels = !isNaN(Number(firstLabel));
  
  let labelIndex: number;
  let displayLabel: string | number;
  
  if (isNumericLabels) {
    // For numeric labels, find the label that matches the current value
    labelIndex = markLabels.findIndex((label: string | number) => Number(label) === currentValue);
    if (labelIndex === -1) return null;
    displayLabel = markLabels[labelIndex];
  } else {
    // For string labels, use the index directly if it's within bounds
    if (index >= markLabels.length) return null;
    labelIndex = index;
    displayLabel = markLabels[index];
  }
  
  let className = 'mark';
  className += labelIndex === 0 ? ' first-mark' : '';
  className += labelIndex === markLabels.length - 1 ? ' last-mark' : '';

  return <div {...restProps} className={className} ref={ref}>{displayLabel}</div>;
});
Mark.displayName = 'Mark'

const Slider = ({ values, minValue, maxValue, step, handleChange, markLabels, id, className, trackColors, }: SliderProps) => {
  const renderThumb = ({ props, }) =>  { return <Thumb {...props} /> }

  const renderMark = ({ props, index, }) => { return <Mark {...props} index={index} markLabels={markLabels} minValue={minValue} step={step} /> }

  const renderTrack = ({ props, children, }) => {
    const { ref, onMouseDown, onTouchStart, style, } = props
    return (
      <Track
        colors={trackColors}
        innerRef={ref}
        key={`track-${id}`}
        maxValue={maxValue}
        minValue={minValue}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={style}
        values={values}
      >
        {children}
      </Track>
    )
  }

  return (
    <div
      className={className}
      id={id}
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}
    >
      <Range
        key={`range-${id}`}
        max={maxValue}
        min={minValue}
        onChange={handleChange}
        renderMark={renderMark}
        renderThumb={renderThumb}
        renderTrack={renderTrack}
        step={step}
        values={values}
      />
    </div>
  );
}

export const OneThumbSlider = ({ value, defaultValue, minValue, maxValue, step, handleChange, markLabels, id, trackColors, }: OneThumbSliderProps) => {
  const isEmpty = value === '' || value === null || value === undefined
  const numericValue = isEmpty ? NaN : (typeof value === 'number' ? value : Number(value))
  const numericDefault = typeof defaultValue === 'number' ? defaultValue : Number(defaultValue)
  const disabled = isEmpty || isNaN(numericValue)
  const fallback = !isNaN(numericDefault) ? numericDefault : minValue
  const values = disabled ? [fallback] : [numericValue]

  let className = "slider-container one-thumb-slider-container"

  if (disabled) {
    className += ' display-as-disabled'
  }

  const activeTrackColors = trackColors || ['#dbdbdb', '#06806b']
  const sliderTrackColors = disabled ? ['#dbdbdb', '#dbdbdb'] : activeTrackColors

  const sliderProps = { values, minValue, maxValue, step, handleChange, markLabels, id, className, trackColors: sliderTrackColors, }

  return <Slider {...sliderProps} />
}

export const TwoThumbSlider = ({ lowerValue, upperValue, minValue, maxValue, step, handleChange, markLabels, id, }: TwoThumbSliderProps) => {
  const values = [lowerValue, upperValue]

  const className = "slider-container two-thumb-slider-container"

  const trackColors = ['#dbdbdb', '#06806b', '#dbdbdb']

  const sliderProps = { values, minValue, maxValue, step, handleChange, markLabels, id, className, trackColors, }

  return <Slider {...sliderProps} />
}
